import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarDays, ClipboardList, ClockAlert, CloudOff, Download, FilePlus, ListFilter, PackageCheck, Plus, ShoppingCart, TriangleAlert, Truck } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit, hasPermission } from '@/lib/permissions';
import { getErrorMessage } from '@/lib/errors';
import { formatMoney, formatNumber, formatPercent } from '@/lib/format';
import { notify } from '@/components/ui/notify';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { StatCard } from '@/components/ui/stat-card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BulkBar, BulkButton } from '@/components/ui/bulk-bar';
import { RowActions } from '@/components/ui/row-actions';
import {
  CardHead, ChipSelect, LinkButton, PhoneKpis, SearchField, Segmented, SfCard, plural, useIsPhone,
} from '@/components/supply-finance/SfUi';
import {
  LPO_EXPORT_COLUMNS, LPO_SORTS, LPO_STATUS_OPTIONS, DN_EXPORT_COLUMNS, NONE, SITE_REQUEST_EXPORT_COLUMNS,
  compareLpos, filterLpos, lpoExportRows, normalizeSiteRequests, procurementKpis, sortLpos, summarizeLpo, supplierSummary,
} from '@/components/supply-finance/procurementModel';
import { LpoTable, SortMenu, useLpoStatusChange } from '@/components/supply-finance/LpoTable';
import { SiteRequestGroups, SiteRequestTip } from '@/components/supply-finance/SiteRequestList';
import { SiteRequestsPanel } from '@/components/supply-finance/SiteRequestsPanel';
import { DeliveryNotesTable } from '@/components/supply-finance/DeliveryNotesTable';
import { LpoFormPanel, ReceiveDeliveryPanel } from '@/components/supply-finance/ProcurementDialogs';
import { useUrlParam } from '@/hooks/useUrlState';
import { datedFilename, downloadCsv } from '@/components/supply-finance/csv';
import { ApprovalLimitNote, LpoDraftsList, useApprovalSettings, useDraftActions } from '@/components/supply-finance/LpoDrafts';
import { isDraftLpo, selectionSummary } from '@/components/supply-finance/draftsModel';
import { MaterialTracePanel } from '@/components/supply-finance/MaterialTracePanel';
import { ChaseSupplierPanel } from '@/components/supply-finance/ChaseSupplierPanel';

const TABS = ['overview', 'orders', 'deliveries', 'site-requests'];
const OVERVIEW_REQUESTS = 6;
// The phone orders head says the order in words (the Sort button is just "Sort" there).
const PHONE_SORT_TEXT = { late: 'most late first', expected: 'by expected date', value: 'biggest first', number: 'by LPO number' };
const DN_EXPORT_PAGE = 500; // the API's largest page
const DN_EXPORT_MAX_PAGES = 20; // 10,000 delivery notes — beyond that the export says it stopped

const tabTriggerClass = 'group relative h-auto shrink-0 rounded-none bg-transparent px-0 pb-[11px] pt-2.5 text-[13.5px] font-semibold text-ink-3 shadow-none ring-offset-0 hover:text-ink-2 data-[state=active]:bg-transparent data-[state=active]:text-ink data-[state=active]:shadow-none after:absolute after:inset-x-0 after:-bottom-px after:h-[2.5px] after:rounded-t after:bg-transparent data-[state=active]:after:bg-brand';
const tabCountClass = 'ml-1.5 rounded-full bg-[color:var(--status-gray-bg)] px-[7px] py-px text-[11px] font-bold tabular-nums text-ink-2 group-data-[state=active]:bg-brand group-data-[state=active]:text-white';

/** 475000 → '475K', 1250000 → '1.3M' (phone KPI tiles only). */
const formatCompactMoney = (value) => {
  const number = Number(value) || 0;
  if (Math.abs(number) >= 1e6) return `${formatNumber(number / 1e6, { maxDecimals: 1 })}M`;
  if (Math.abs(number) >= 1e4) return `${formatNumber(number / 1e3, { decimals: 0 })}K`;
  return formatNumber(number, { decimals: 0 });
};

const settledData = (result, fallback) => (result.status === 'fulfilled' ? result.value.data : fallback);
const settledError = (result, fallback) => (result.status === 'rejected' ? getErrorMessage(result.reason, fallback) : '');

/** Every delivery note matching the table's filters, page by page (newest delivery first). */
async function fetchDeliveryNotesForExport(filters) {
  const items = [];
  let page = 1;
  let total = 0;
  let totalPages = 1;
  do {
    // Pages are fetched one after another on purpose (one request at a time).
    const res = await api.get('/delivery-notes', { params: { ...filters, page, limit: DN_EXPORT_PAGE, sort: 'delivery_date' } });
    const data = res.data || {};
    items.push(...(Array.isArray(data.items) ? data.items : []));
    total = data.total ?? items.length;
    totalPages = data.total_pages || 1;
    page += 1;
  } while (page <= totalPages && page <= DN_EXPORT_MAX_PAGES);
  return { items, total };
}

export default function ProcurementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const tab = TABS.includes(requestedTab) ? requestedTab : 'overview';

  const [lpos, setLpos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [siteRequests, setSiteRequests] = useState([]);
  const [dnCounts, setDnCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // Per-source load errors, so a failed list reads as "couldn't load", never as an empty one.
  const [sourceErrors, setSourceErrors] = useState({});
  const [requestsProject, setRequestsProject] = useState(null);
  const dnFiltersRef = useRef({});
  // Forms: New / Edit LPO (focus 'project' = "No project linked" → link it) and Receive delivery (from an LPO or blank).
  const [lpoPanel, setLpoPanel] = useState({ open: false, mode: 'create', lpo: null, focus: null });
  const [dnPanel, setDnPanel] = useState({ open: false, lpoId: '' });
  const [dnRefreshKey, setDnRefreshKey] = useState(0);
  const [sortParam, setSortParam] = useUrlParam('sort', 'late');
  const sortKey = LPO_SORTS.some((option) => option.key === sortParam) ? sortParam : 'late';
  const [expanded, setExpanded] = useState(() => new Set());

  // Links open the orders pre-filtered (read once on load): ?filter=late (past the expected date with
  // quantity still to come), ?filter=unlinked (no project, or a deleted one) and ?lpo=<number>.
  const linkFilter = String(searchParams.get('filter') || '').trim().toLowerCase();
  const linkLpo = String(searchParams.get('lpo') || '').trim();
  const [view, setView] = useState(() => (linkLpo || linkFilter === 'unlinked' ? 'all' : 'open'));
  const [search, setSearch] = useState(() => linkLpo);
  const [projectFilter, setProjectFilter] = useState(() => (linkFilter === 'unlinked' ? NONE : null));
  const [supplierFilter, setSupplierFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => (linkFilter === 'late' ? 'Late' : null));
  const ordersRef = useRef(null);
  const isPhone = useIsPhone();
  const [phoneFiltersOpen, setPhoneFiltersOpen] = useState(false);
  // A filtered link lands on the orders list once it has loaded (not on the KPI strip above it).
  const scrolledToLinkRef = useRef(false);
  useEffect(() => {
    if (scrolledToLinkRef.current || loading || !(linkFilter || linkLpo)) return;
    scrolledToLinkRef.current = true;
    ordersRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [linkFilter, linkLpo, loading]);

  // Edit level per module (Admin › Access View / Edit; Viewers are read-only): the server refuses the same writes.
  const canReceive = canEdit(user, 'material');
  const canCreateLpo = canEdit(user, 'procurement');
  const canEditRequests = canEdit(user, 'material');

  const setTab = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'overview') params.delete('tab'); else params.set('tab', next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const fetchData = useCallback(async () => {
    // The catalog is searched by the forms' part pickers as people type (it is paged on the server).
    const [lRes, pRes, srRes, dnRes] = await Promise.allSettled([
      api.get('/lpos'),
      api.get('/projects'),
      api.get('/site-requests'),
      api.get('/delivery-notes', { params: { page: 1, limit: 1, sort: 'delivery_date' } }),
    ]);
    setLoadError(settledError(lRes, 'Could not load orders'));
    setSourceErrors({
      siteRequests: settledError(srRes, 'Could not load site requests'),
      projects: settledError(pRes, 'Could not load projects'),
      deliveries: settledError(dnRes, 'Could not load delivery notes'),
    });
    setLpos(settledData(lRes, []) || []);
    setProjects(settledData(pRes, []) || []);
    setSiteRequests(settledData(srRes, []) || []);
    const dnData = settledData(dnRes, null);
    setDnCounts(dnData ? (dnData.counts || { all: dnData.total ?? 0, supplier_missing: 0 }) : null);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refreshDeliveries = useCallback(() => {
    setDnRefreshKey((value) => value + 1);
    fetchData();
  }, [fetchData]);

  // 22 #9: drafts (Draft / Pending approval / Approved) are not orders yet — they live in "Drafts & approvals".
  const draftLpos = useMemo(() => lpos.filter(isDraftLpo), [lpos]);
  const issuedLpos = useMemo(() => lpos.filter((lpo) => !isDraftLpo(lpo)), [lpos]);
  const summaries = useMemo(() => issuedLpos.map((lpo) => summarizeLpo(lpo)).sort(compareLpos), [issuedLpos]);
  const openRequests = useMemo(() => normalizeSiteRequests(siteRequests), [siteRequests]);
  const kpis = useMemo(() => procurementKpis(summaries, openRequests), [summaries, openRequests]);

  const visibleLpos = useMemo(
    () => sortLpos(filterLpos(summaries, { view, search, project: projectFilter, supplier: supplierFilter, status: statusFilter }), sortKey),
    [summaries, view, search, projectFilter, supplierFilter, statusFilter, sortKey]
  );
  const toggleLpo = useCallback((id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);
  // A ?lpo=<number> link opens that order once the list has loaded.
  const openedLinkRef = useRef(false);
  useEffect(() => {
    if (openedLinkRef.current || loading || !linkLpo) return;
    openedLinkRef.current = true;
    const match = summaries.filter((item) => item.number.toLowerCase() === linkLpo.toLowerCase());
    if (match.length === 1) setExpanded(new Set([match[0].id]));
    const draft = draftLpos.find((item) => String(item.lpo_number || '').toLowerCase() === linkLpo.toLowerCase());
    if (draft && !match.length) {
      // A notification ("LPO-2026-004 needs your approval") opens the draft in Drafts & approvals.
      setHighlightDraft(draft.id);
      setSearch('');
      if (tab !== 'orders') setTab('orders');
      setTimeout(() => document.querySelector(`[data-testid="lpo-draft-${draft.id}"]`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 50);
    }
  }, [linkLpo, loading, summaries, draftLpos, tab, setTab]);
  const changeLpoStatus = useLpoStatusChange(fetchData);
  const approvals = useApprovalSettings(hasPermission(user, 'procurement'));
  const [runDraftAction, draftDialogs] = useDraftActions(fetchData);
  const [selectedRequests, setSelectedRequests] = useState(() => new Set());
  const [creatingDrafts, setCreatingDrafts] = useState(false);
  const [highlightDraft, setHighlightDraft] = useState(null);
  const [trace, setTrace] = useState(null);
  const [chase, setChase] = useState(null);
  const openTrace = useCallback((target) => setTrace(target), []);
  const openChase = useCallback((item) => setChase({ id: item.id, number: item.number, supplier: item.supplier }), []);
  const editDraft = useCallback((draft) => setLpoPanel({ open: true, mode: 'edit', lpo: draft, focus: null }), []);
  const openReceive = useCallback((item) => setDnPanel({ open: true, lpoId: item?.id || '' }), []);
  const openEdit = useCallback((item, focus = null) => setLpoPanel({ open: true, mode: 'edit', lpo: item.lpo, focus }), []);
  const supplierNames = useMemo(() => summaries.map((item) => item.supplier).filter(Boolean), [summaries]);
  const hasFilters = Boolean(search.trim() || projectFilter || supplierFilter || statusFilter);
  const clearFilters = () => { setSearch(''); setProjectFilter(null); setSupplierFilter(null); setStatusFilter(null); };

  const projectOptions = useMemo(() => {
    const options = new Map();
    summaries.forEach((item) => {
      const key = item.projectKey;
      const label = key === NONE ? 'No project linked' : item.projectName;
      const entry = options.get(key) || { value: key, label, count: 0, muted: key === NONE };
      entry.count += 1;
      options.set(key, entry);
    });
    // A ?filter=unlinked link keeps its readable label even when every LPO has a project.
    if (projectFilter === NONE && !options.has(NONE)) options.set(NONE, { value: NONE, label: 'No project linked', count: 0, muted: true });
    return [...options.values()].sort((a, b) => (a.muted - b.muted) || a.label.localeCompare(b.label));
  }, [projectFilter, summaries]);
  const supplierOptions = useMemo(() => {
    const options = new Map();
    summaries.forEach((item) => {
      const key = item.supplierKey;
      const entry = options.get(key) || { value: key, label: key === NONE ? 'Supplier missing' : item.supplier, count: 0, muted: key === NONE };
      entry.count += 1;
      options.set(key, entry);
    });
    return [...options.values()].sort((a, b) => (a.muted - b.muted) || a.label.localeCompare(b.label));
  }, [summaries]);
  const statusOptions = useMemo(() => LPO_STATUS_OPTIONS.map((status) => ({
    value: status,
    label: status,
    count: summaries.filter((item) => (status === 'Late' ? item.isLate : item.deliveryState === status)).length,
  })), [summaries]);

  const showLate = () => {
    setView('open');
    setStatusFilter('Late');
    if (tab !== 'overview' && tab !== 'orders') setTab('overview');
    setTimeout(() => ordersRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0);
  };

  const requestsError = sourceErrors.siteRequests || '';
  // 22 #9: pick requests → "Create LPO draft" (one draft per supplier and project, priced from the last LPO).
  const toggleRequest = useCallback((id) => setSelectedRequests((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);
  const requestSelection = canCreateLpo ? { selectedIds: selectedRequests, onToggle: toggleRequest } : null;
  const picked = useMemo(() => selectionSummary(openRequests, selectedRequests), [openRequests, selectedRequests]);
  useEffect(() => {
    // Requests that left the queue (delivered, drafted elsewhere) drop out of the selection.
    setSelectedRequests((prev) => {
      const keep = new Set(openRequests.filter((request) => prev.has(request.id) && !request.lpo).map((request) => request.id));
      return keep.size === prev.size ? prev : keep;
    });
  }, [openRequests]);
  const createDrafts = async () => {
    if (!picked.ids.length || creatingDrafts) return;
    setCreatingDrafts(true);
    try {
      const res = await api.post('/lpos/drafts-from-requests', { site_request_ids: picked.ids });
      const drafts = res.data?.drafts || [];
      setSelectedRequests(new Set());
      notify.success(drafts.length === 1 ? `Draft ${drafts[0].lpo_number} created` : `${drafts.length} LPO drafts created`, {
        description: drafts.map((draft) => `${draft.lpo_number} · ${draft.supplier || 'supplier missing'} · ${formatMoney(draft.approval_amount ?? draft.total_value, { decimals: 0 })}`).join('\n'),
      });
      await fetchData();
    } catch (err) {
      notify.error('Could not create the draft', { description: getErrorMessage(err) });
      await fetchData();
    } finally {
      setCreatingDrafts(false);
    }
  };
  const pendingMine = approvals.can_approve ? draftLpos.filter((draft) => String(draft.status) === 'Pending approval').length : 0;
  const panelRequests = useMemo(
    () => (requestsProject ? openRequests.filter((request) => request.project_id === requestsProject) : openRequests),
    [openRequests, requestsProject]
  );

  // Export = what the open tab shows: the Deliveries table's filters, the Site requests project filter,
  // or the Orders filters (Overview exports the orders too).
  const exportCurrent = async () => {
    try {
      if (tab === 'deliveries') {
        const filters = dnFiltersRef.current || {};
        const filtered = Object.keys(filters).length > 0;
        const { items, total } = await fetchDeliveryNotesForExport(filters);
        downloadCsv(datedFilename('procurement-deliveries'), items, DN_EXPORT_COLUMNS);
        if (items.length < total) {
          notify.warning(`Exported the newest ${formatNumber(items.length)} of ${plural(total, 'delivery note')}`, { description: 'Narrow the table with a filter to export the rest.' });
        } else {
          notify.success(`Exported ${plural(items.length, 'delivery note')}`, filtered ? { description: 'With the table’s filters' } : undefined);
        }
      } else if (tab === 'site-requests') {
        if (requestsError) {
          notify.error('Could not export', { description: 'Site requests did not load. Try again first.' });
          return;
        }
        downloadCsv(datedFilename('procurement-site-requests'), panelRequests, SITE_REQUEST_EXPORT_COLUMNS);
        notify.success(`Exported ${plural(panelRequests.length, 'site request')}`, requestsProject ? { description: 'For the selected project' } : undefined);
      } else {
        downloadCsv(datedFilename('procurement-orders'), lpoExportRows(visibleLpos), LPO_EXPORT_COLUMNS);
        notify.success(`Exported ${plural(visibleLpos.length, 'order')}`);
      }
    } catch (err) {
      notify.error('Could not export', { description: getErrorMessage(err) });
    }
  };

  const openProjects = () => navigate('/projects');
  const openCount = kpis.openCount;
  const allCount = summaries.length;
  const lateLabel = `${formatNumber(kpis.lateCount)} ${kpis.lateCount === 1 ? 'LPO' : 'LPOs'} late`;
  const overdueLabel = `${formatNumber(kpis.siteRequestsOverdue)} site ${kpis.siteRequestsOverdue === 1 ? 'request' : 'requests'} overdue`;

  const openNewLpo = () => setLpoPanel({ open: true, mode: 'create', lpo: null, focus: null });
  const phoneActions = isPhone ? (
    <span className="ml-auto flex items-center gap-1.5" data-testid="procurement-phone-actions">
      <RowActions
        size="md"
        label="More actions: receive delivery, export"
        className="border border-line bg-surface text-ink-2"
        data-testid="procurement-more"
        items={[
          canReceive && { label: 'Receive delivery', icon: Truck, onSelect: () => openReceive(null), testId: 'create-dn-btn' },
          { label: 'Export this list', icon: Download, onSelect: exportCurrent, testId: 'procurement-export' },
        ]}
      />
      {canCreateLpo ? (
        <Button onClick={openNewLpo} className="px-3" data-testid="create-lpo-btn">
          <Plus aria-hidden="true" /> New LPO
        </Button>
      ) : null}
    </span>
  ) : null;

  const header = (
    <PageHeader
      title="Procurement"
      count={loading ? undefined : formatNumber(openCount)}
      subtitle={isPhone ? undefined : 'Orders, deliveries and site requests across all projects'}
      titleAdornment={phoneActions}
      alerts={[
        kpis.lateCount > 0 && { key: 'late', label: lateLabel, tone: 'red', onClick: showLate },
        kpis.siteRequestsOverdue > 0 && { key: 'overdue', label: overdueLabel, tone: 'red', onClick: () => setTab('site-requests') },
        pendingMine > 0 && { key: 'approvals', label: `${plural(pendingMine, 'LPO')} awaiting your approval`, tone: 'amber', onClick: () => setTab('orders') },
      ]}
      actions={isPhone ? null : (
        <>
          <Button variant="ghost" onClick={exportCurrent} className="px-2.5 text-ink-2 sm:px-3.5" aria-label="Export" data-testid="procurement-export">
            <Download aria-hidden="true" /><span className="hidden sm:inline">Export</span>
          </Button>
          {canReceive ? (
            <Button variant="outline" onClick={() => openReceive(null)} className="px-2.5 text-ink sm:px-3.5" aria-label="Receive delivery" data-testid="create-dn-btn">
              <Truck aria-hidden="true" /><span className="hidden sm:inline">Receive delivery</span>
            </Button>
          ) : null}
          {canCreateLpo ? (
            <Button onClick={openNewLpo} data-testid="create-lpo-btn">
              <Plus aria-hidden="true" /> New LPO
            </Button>
          ) : null}
        </>
      )}
    />
  );

  const activeFilters = [search.trim(), projectFilter, supplierFilter, statusFilter].filter(Boolean).length;
  const showFilterFields = !isPhone || phoneFiltersOpen || hasFilters;
  const filterRow = (
    <div className="flex flex-wrap items-center gap-2" data-testid="procurement-filters">
      <Segmented
        ariaLabel="Which orders"
        value={view}
        onChange={setView}
        options={[
          { key: 'open', label: 'Open', count: openCount, testId: 'lpo-view-open' },
          { key: 'all', label: 'All', count: allCount, testId: 'lpo-view-all' },
        ]}
      />
      {isPhone ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPhoneFiltersOpen((open) => !open)}
          aria-expanded={showFilterFields}
          aria-controls="procurement-filter-fields"
          className="ml-auto"
          data-testid="procurement-filter-toggle"
        >
          <ListFilter aria-hidden="true" /> Filter{activeFilters ? <span className="rounded-full bg-brand px-1.5 text-[11px] font-bold text-white tabular-nums">{formatNumber(activeFilters)}</span> : null}
        </Button>
      ) : null}
      {showFilterFields ? (
        <div id="procurement-filter-fields" className="contents">
          <SearchField value={search} onChange={setSearch} placeholder="Search LPO, supplier, part no." label="Search orders" shortcut className="min-w-0 flex-1 basis-[180px] sm:w-[280px] sm:flex-none" testId="lpo-search" />
          <ChipSelect label="Project" value={projectFilter} options={projectOptions} onChange={setProjectFilter} testId="lpo-filter-project" />
          <ChipSelect label="Supplier" value={supplierFilter} options={supplierOptions} onChange={setSupplierFilter} testId="lpo-filter-supplier" />
          <ChipSelect label="Status" value={statusFilter} options={statusOptions} onChange={setStatusFilter} testId="lpo-filter-status" />
        </div>
      ) : null}
      {hasFilters ? <LinkButton onClick={clearFilters} className="px-1">Clear filters</LinkButton> : null}
      <span className="ml-auto hidden text-[12.5px] text-ink-3 md:inline" data-testid="procurement-scope">
        {plural(openCount, 'open LPO')}{dnCounts ? ` · ${formatNumber(dnCounts.all)} DNs` : ''}
      </span>
    </div>
  );

  const deliveredPct = Math.round(kpis.deliveredPct);
  const kpiStrip = isPhone ? (
    <PhoneKpis
      testId="procurement-kpis"
      items={[
        { key: 'late', value: formatNumber(kpis.lateCount), label: kpis.lateCount === 1 ? 'LPO late' : 'LPOs late', tone: kpis.lateCount ? 'red' : null, onClick: kpis.lateCount ? showLate : undefined },
        requestsError
          ? { key: 'overdue', value: '—', label: 'site requests didn’t load', onClick: () => setTab('site-requests') }
          : { key: 'overdue', value: formatNumber(kpis.siteRequestsOverdue), label: kpis.siteRequestsOverdue === 1 ? 'request overdue' : 'requests overdue', tone: kpis.siteRequestsOverdue ? 'red' : null, onClick: () => setTab('site-requests') },
        { key: 'open', prefix: 'AED', value: formatCompactMoney(kpis.openValue), label: 'open orders', onClick: () => setTab('orders') },
      ]}
    />
  ) : (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="procurement-kpis">
      <StatCard
        label="Open orders"
        icon={ShoppingCart}
        tone="navy"
        prefix="AED"
        value={formatNumber(kpis.openValue, { decimals: 0 })}
        meta={kpis.suppliers.length
          ? <span title={kpis.suppliers.join(', ')} data-testid="kpi-open-orders-suppliers">{supplierSummary(kpis.suppliers)}</span>
          : 'No open orders'}
        onClick={() => setTab('orders')}
        loading={loading}
        data-testid="kpi-open-orders"
      />
      <StatCard
        label="Late deliveries"
        icon={ClockAlert}
        tone={kpis.lateCount ? 'red' : 'gray'}
        value={formatNumber(kpis.lateCount)}
        unit={kpis.lateCount === 1 ? 'LPO' : 'LPOs'}
        valueTone={kpis.lateCount ? 'red' : undefined}
        meta={kpis.oldestLate
          ? `${plural(kpis.lateLines, 'line')} · oldest ${formatNumber(kpis.oldestLate.daysLate)} d (${kpis.oldestLate.number})`
          : 'Nothing past its expected date'}
        onClick={kpis.lateCount ? showLate : undefined}
        loading={loading}
        data-testid="kpi-late-deliveries"
      />
      <StatCard
        label="Delivered on open LPOs"
        icon={PackageCheck}
        tone="green"
        value={formatPercent(deliveredPct)}
        progress={deliveredPct}
        progressTone="green"
        meta={kpis.ordered ? `${formatNumber(kpis.delivered)} of ${formatNumber(kpis.ordered)} pcs` : 'No quantities on open orders'}
        loading={loading}
        data-testid="kpi-delivered"
      />
      <StatCard
        label="Site requests"
        icon={ClipboardList}
        tone="amber"
        value={requestsError ? '—' : formatNumber(kpis.siteRequestCount)}
        meta={requestsError ? <span className="font-semibold text-status-red-ink">Couldn’t load site requests</span> : kpis.siteRequestCount ? (
          <>
            {kpis.siteRequestsOverdue ? <span className="font-semibold text-status-red-ink">{formatNumber(kpis.siteRequestsOverdue)} overdue</span> : <span>0 overdue</span>}
            {' · '}{formatNumber(kpis.siteRequestsThisWeek)} due this week
          </>
        ) : 'None open'}
        onClick={() => setTab('site-requests')}
        loading={loading}
        data-testid="kpi-site-requests"
      />
    </div>
  );

  const ordersCard = (full) => (
    <SfCard data-testid="lpo-list" className="max-sm:border-0 max-sm:bg-transparent max-sm:shadow-none">
      <CardHead
        className="max-sm:min-h-0 max-sm:flex-nowrap max-sm:border-b-0 max-sm:px-1 max-sm:py-0"
        title={view === 'open' ? 'Open orders' : 'All orders'}
        // Phones: one line ("Open orders · 4 · Sort") — the tab and the tiles already say the count and value.
        sub={isPhone
          ? `${formatNumber(visibleLpos.length)} · ${PHONE_SORT_TEXT[sortKey] || PHONE_SORT_TEXT.late}`
          : `${plural(visibleLpos.length, 'LPO/LOI', 'LPO/LOI')} · ${formatMoney(visibleLpos.reduce((sum, item) => sum + item.value, 0), { decimals: 0 })}`}
        right={(
          <>
            {statusFilter === 'Late' && !full ? <span className="text-[12px] font-semibold text-status-red-ink">Showing late orders</span> : null}
            {visibleLpos.length > 1 ? <SortMenu value={sortKey} onChange={setSortParam} compact={isPhone} /> : null}
          </>
        )}
      />
      {loadError && !lpos.length ? (
        <p className="px-[18px] py-6 text-[13px] text-status-red-ink" role="alert">{loadError}</p>
      ) : (
        <LpoTable
          items={visibleLpos}
          expanded={expanded}
          onToggle={toggleLpo}
          canEdit={canCreateLpo}
          canReceive={canReceive}
          onReceive={openReceive}
          onEdit={(item) => openEdit(item)}
          onLinkProject={(item) => openEdit(item, 'project')}
          onStatus={changeLpoStatus}
          onChase={canCreateLpo ? openChase : undefined}
          onTrace={openTrace}
          refreshKey={dnRefreshKey}
          filtered={hasFilters}
          onClearFilters={clearFilters}
          emptyTitle={view === 'open' ? 'No open orders' : 'No orders yet'}
        />
      )}
      {visibleLpos.length ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line-2 px-4 py-2.5 text-[12.5px] text-ink-3 sm:px-[18px]">
          <span>Total · {plural(visibleLpos.length, view === 'open' ? 'open LPO' : 'LPO')}</span>
          <span className="tabular-nums">
            <b className="font-semibold text-ink">{formatMoney(visibleLpos.reduce((sum, item) => sum + item.value, 0))}</b>
            {' · '}{formatNumber(visibleLpos.reduce((sum, item) => sum + item.delivered, 0))} of {formatNumber(visibleLpos.reduce((sum, item) => sum + item.ordered, 0))} pcs delivered
          </span>
        </div>
      ) : null}
    </SfCard>
  );

  const shownRequests = openRequests.slice(0, OVERVIEW_REQUESTS);
  const clearRequests = () => setSelectedRequests(new Set());
  const requestsBulkBar = (
    <BulkBar
      count={picked.count}
      label={(
        <span className="text-[12.5px]" data-testid="requests-bulk-label">
          {plural(picked.count, 'selected', 'selected')}
          {picked.qty ? <span className="font-medium text-white/70"> · {formatNumber(picked.qty)}{picked.unit ? ` ${picked.unit}` : ''}</span> : null}
        </span>
      )}
      testId="requests-bulkbar"
      className="mb-1 mt-2"
    >
      {/* Clear sits before the main action (as in the approved design); Esc on either button clears too. */}
      <span className="flex items-center gap-1.5" onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); clearRequests(); } }}>
        <BulkButton onClick={clearRequests} title="Clear selection (Esc)" aria-label="Clear selection" className="px-[9px]" data-testid="requests-bulkbar-clear">
          Clear
        </BulkButton>
        <BulkButton icon={FilePlus} onClick={createDrafts} disabled={creatingDrafts} className="gap-[5px] bg-lime px-[9px] text-[color:var(--navy)] hover:bg-lime-400" data-testid="create-lpo-draft">
          {creatingDrafts ? 'Creating…' : 'Create LPO draft'}
        </BulkButton>
      </span>
    </BulkBar>
  );
  const requestsBelowList = (compact) => (canCreateLpo && !requestsError ? (
    <>
      {requestsBulkBar}
      {(openRequests.length || draftLpos.length) ? (
        <div className="space-y-2 px-4 pb-3 pt-2 sm:px-[18px]" data-testid="requests-drafts">
          <ApprovalLimitNote settings={approvals} onSaved={fetchData} />
          <LpoDraftsList
            drafts={compact ? draftLpos.slice(0, 3) : draftLpos}
            threshold={approvals.threshold}
            canApprove={approvals.can_approve}
            canEdit={canCreateLpo}
            onAction={runDraftAction}
            onEdit={editDraft}
            compact
            testId={compact ? 'overview-lpo-drafts' : 'tab-lpo-drafts'}
          />
          {compact && draftLpos.length > 3 ? <LinkButton onClick={() => setTab('orders')}>All {formatNumber(draftLpos.length)} drafts</LinkButton> : null}
        </div>
      ) : null}
    </>
  ) : null);
  const draftsCard = draftLpos.length ? (
    <SfCard data-testid="lpo-drafts-card">
      <CardHead
        icon={FilePlus}
        title="Drafts & approvals"
        sub={`${plural(draftLpos.length, 'draft')} · not sent to suppliers yet`}
        right={<ApprovalLimitNote settings={approvals} onSaved={fetchData} />}
      />
      <div className="p-3 sm:p-4">
        <LpoDraftsList
          drafts={draftLpos}
          threshold={approvals.threshold}
          canApprove={approvals.can_approve}
          canEdit={canCreateLpo}
          onAction={runDraftAction}
          onEdit={editDraft}
          highlightId={highlightDraft}
          testId="orders-lpo-drafts"
        />
      </div>
    </SfCard>
  ) : null;
  const openCalendar = () => setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    params.set('tab', 'site-requests');
    params.set('view', 'calendar');
    return params;
  }, { replace: true });
  const requestsCard = (
    <SfCard data-testid="site-requests-card">
      <CardHead
        title="Site requests"
        sub={requestsError ? undefined : `${formatNumber(openRequests.length)} open`}
        right={requestsError ? null : (
          <Button variant="ghost" size="sm" onClick={openCalendar} data-testid="site-requests-calendar-btn">
            <CalendarDays aria-hidden="true" /> Calendar
          </Button>
        )}
      />
      {requestsError ? (
        <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't load site requests" text={requestsError}
          action={<Button variant="outline" size="sm" onClick={fetchData}>Try again</Button>} data-testid="site-requests-error" />
      ) : (
        <SiteRequestGroups
          requests={shownRequests}
          canEdit={canEditRequests}
          onChanged={fetchData}
          selection={requestSelection}
          onTrace={(request) => openTrace({ part: request.part_number, projectId: request.project_id, projectName: request.project_name, description: request.description })}
          emptyAction={<Button variant="outline" size="sm" onClick={openProjects}>Open Projects</Button>}
        />
      )}
      {requestsBelowList(true)}
      {!requestsError && openRequests.length > OVERVIEW_REQUESTS ? (
        <div className="border-t border-line-2 px-4 py-2.5 sm:px-[18px]">
          <LinkButton onClick={() => setTab('site-requests')}>View all {formatNumber(openRequests.length)} requests</LinkButton>
        </div>
      ) : null}
      {!requestsError && openRequests.length ? <SiteRequestTip onOpenProjects={openProjects} /> : null}
    </SfCard>
  );

  // Projects feed the filters and the New LPO / Receive delivery forms: say when they are missing.
  const missingSources = [
    sourceErrors.projects && 'projects',
  ].filter(Boolean);
  const sourceNote = !loading && missingSources.length ? (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-[color:var(--status-amber-bg)] bg-status-amber-bg px-3.5 py-2 text-[12.5px] text-status-amber-ink" role="alert" data-testid="procurement-source-errors">
      <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        Couldn’t load {missingSources.join(' and ')} — project filters and the order / delivery forms may be incomplete.
      </span>
      <LinkButton onClick={fetchData}>Try again</LinkButton>
    </div>
  ) : null;

  return (
    <div data-testid="procurement-page" className="space-y-4">
      {header}
      {sourceNote}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        {isPhone ? (
          <Segmented
            fullWidth
            ariaLabel="Procurement views"
            value={tab === 'deliveries' || tab === 'site-requests' ? tab : 'overview'}
            onChange={setTab}
            className="p-1 [&>button]:h-9 [&>button]:px-1.5 [&>button]:text-[13.5px]"
            testId="procurement-phone-tabs"
            options={[
              { key: 'overview', label: 'Orders', count: openCount, testId: 'phone-tab-orders' },
              { key: 'deliveries', label: 'Deliveries', count: dnCounts ? dnCounts.all : undefined, testId: 'phone-tab-deliveries' },
              {
                key: 'site-requests',
                label: 'Requests',
                count: requestsError ? undefined : openRequests.length,
                dot: kpis.siteRequestsOverdue ? 'red' : undefined,
                title: kpis.siteRequestsOverdue ? overdueLabel : undefined,
                testId: 'phone-tab-requests',
              },
            ]}
          />
        ) : (
        <div className="border-b border-line">
          <TabsList className="h-auto w-full justify-start gap-[22px] overflow-x-auto rounded-none bg-transparent p-0 text-ink-3">
            <TabsTrigger value="overview" className={tabTriggerClass} data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="orders" className={tabTriggerClass} data-testid="tab-orders">
              Orders<span className={tabCountClass}>{formatNumber(openCount)}</span>
            </TabsTrigger>
            <TabsTrigger value="deliveries" className={tabTriggerClass} data-testid="tab-deliveries">
              Deliveries{dnCounts ? <span className={tabCountClass}>{formatNumber(dnCounts.all)}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="site-requests" className={tabTriggerClass} data-testid="tab-site-requests">
              Site requests{requestsError ? null : <span className={tabCountClass}>{formatNumber(openRequests.length)}</span>}
              {kpis.siteRequestsOverdue ? (
                <span className="ml-1.5 inline-block h-[7px] w-[7px] rounded-full bg-status-red" title={overdueLabel}>
                  <span className="sr-only">({formatNumber(kpis.siteRequestsOverdue)} overdue)</span>
                </span>
              ) : null}
            </TabsTrigger>
          </TabsList>
        </div>
        )}

        <TabsContent value="overview" className="mt-3 space-y-4">
          {isPhone ? kpiStrip : filterRow}
          {isPhone ? filterRow : kpiStrip}
          <div ref={ordersRef} className="grid scroll-mt-24 grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            {ordersCard(false)}
            {requestsCard}
          </div>
          <DeliveryNotesTable
            pageSize={8}
            title="Recent deliveries"
            variant="recent"
            projects={projects}
            refreshKey={dnRefreshKey}
            onViewAll={() => setTab('deliveries')}
          />
        </TabsContent>

        <TabsContent value="orders" className="mt-3 space-y-4">
          {filterRow}
          {draftsCard}
          {ordersCard(true)}
        </TabsContent>

        <TabsContent value="deliveries" className="mt-3">
          <DeliveryNotesTable
            pageSize={25}
            title="Delivery notes"
            variant="full"
            projects={projects}
            refreshKey={dnRefreshKey}
            onFiltersChange={(filters) => { dnFiltersRef.current = filters; }}
          />
        </TabsContent>

        <TabsContent value="site-requests" className="mt-3">
          <SiteRequestsPanel
            requests={openRequests}
            projects={projects}
            canEdit={canEditRequests}
            onChanged={fetchData}
            onOpenProjects={openProjects}
            projectId={requestsProject}
            onProjectChange={setRequestsProject}
            error={requestsError}
            onRetry={fetchData}
            selection={requestSelection}
            onTrace={(request) => openTrace({ part: request.part_number, projectId: request.project_id, projectName: request.project_name, description: request.description })}
            belowList={requestsBelowList(false)}
          />
        </TabsContent>
      </Tabs>

      <LpoFormPanel
        open={lpoPanel.open}
        onOpenChange={(open) => setLpoPanel((prev) => ({ ...prev, open }))}
        mode={lpoPanel.mode}
        lpo={lpoPanel.lpo}
        focus={lpoPanel.focus}
        projects={projects}
        suppliers={supplierNames}
        onSaved={refreshDeliveries}
      />
      <ReceiveDeliveryPanel
        open={dnPanel.open}
        onOpenChange={(open) => setDnPanel((prev) => ({ ...prev, open }))}
        lpoId={dnPanel.lpoId}
        lpos={issuedLpos}
        projects={projects}
        suppliers={supplierNames}
        userName={user?.name || ''}
        onCreated={refreshDeliveries}
      />
      {draftDialogs}
      <MaterialTracePanel
        open={Boolean(trace)}
        onOpenChange={(open) => { if (!open) setTrace(null); }}
        part={trace?.part}
        projectId={trace?.projectId}
        projectName={trace?.projectName}
        description={trace?.description}
      />
      <ChaseSupplierPanel
        open={Boolean(chase)}
        onOpenChange={(open) => { if (!open) setChase(null); }}
        lpoId={chase?.id}
        number={chase?.number}
        supplier={chase?.supplier}
        onChanged={refreshDeliveries}
      />
      <span className="sr-only" aria-live="polite">{loading ? 'Loading procurement data' : ''}</span>
    </div>
  );
}
